import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

const BlogPost17: React.FC = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');

  const handleGenerateName = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim() && surname.trim()) {
      navigate('/chinese-last-names', { 
        state: { 
          firstName: firstName.trim(),
          surname: surname.trim(),
          autoGenerate: true 
        } 
      });
    }
  };

  return (
    <>
      <SeoMeta
        title="Chinese Last Name Meanings Explained | Complete Guide"
        description="Curious what your Chinese surname means? Discover why 王 means 'king', 李 relates to plum trees, and 张 means 'stretch'. Learn the fascinating origins of 100+ Chinese last names, from ancient states to geographical locations. Understand how surnames reflect Chinese history, family lineage, and cultural values. Perfect for genealogy research, cultural learning, or understanding your Chinese heritage."
        keywords="chinese last name meanings, chinese surname meanings, chinese family name meanings, common chinese last names meaning, chinese last name origins"
        pathname="/blog/chinese-last-name-meanings"
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </nav>

        <header className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Surnames Guide
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Chinese Last Name Meanings Explained
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Explore the fascinating meanings and origins behind Chinese surnames. Learn about the historical significance and cultural importance of <strong>Chinese last name meanings</strong> that have shaped Chinese culture for thousands of years.
            </p>
          </div>
        </header>

        <article className="max-w-4xl mx-auto px-4 pb-16">
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding Chinese Surnames</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Chinese surnames carry deep historical and cultural significance. Most <strong>Chinese last name meanings</strong> have origins dating back thousands of years, often connected to ancient states, occupations, or geographical locations. Understanding these meanings provides insight into Chinese history and culture.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Unlike Western surnames, which often evolved from occupations or locations, Chinese surnames frequently trace their origins to ancient dynasties, legendary figures, or philosophical concepts. Each <strong>Chinese surname meaning</strong> tells a story about Chinese civilization.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Most Common Chinese Last Names and Their Meanings</h2>
            <div className="space-y-6">
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">李 (Lǐ) - Plum Tree</h3>
                <p className="text-gray-700">
                  One of the most common Chinese surnames, Li originally meant "plum tree." It was the imperial surname of the Tang Dynasty and represents wisdom and longevity. The <strong>Chinese last name meaning</strong> reflects resilience and beauty.
                </p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">王 (Wáng) - King</h3>
                <p className="text-gray-700">
                  Meaning "king" or "monarch," Wang has a history dating back over 4,000 years. This <strong>Chinese surname meaning</strong> represents nobility and leadership, originally given to descendants of ancient Chinese rulers.
                </p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">张 (Zhāng) - To Draw a Bow</h3>
                <p className="text-gray-700">
                  Originally meant "to draw a bow," Zhang was derived from an ancient officer title during the Zhou Dynasty. The <strong>Chinese last name meaning</strong> is associated with archery and military strategy.
                </p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">刘 (Liú) - Kill/Destroy</h3>
                <p className="text-gray-700">
                  Though originally associated with "kill," Liu gained prominence as the imperial surname of the Han Dynasty. This <strong>Chinese surname meaning</strong> evolved to represent strength and determination.
                </p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">陈 (Chén) - Morning/Exhibition</h3>
                <p className="text-gray-700">
                  Originated from the State of Chen, this surname represents "morning" or "exhibition." The <strong>Chinese last name meaning</strong> is associated with commerce and scholarship, reflecting the Chen family's historical achievements.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Categories of Chinese Surname Origins</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Geographical Origins</h3>
                <p className="text-gray-700 text-sm">
                  Many <strong>Chinese last name meanings</strong> derive from ancient states, cities, or geographical features. Examples include 陈 (Chen) from the State of Chen and 周 (Zhou) from the Zhou Dynasty.
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Occupational Origins</h3>
                <p className="text-gray-700 text-sm">
                  Some surnames originated from occupations or titles. The <strong>Chinese surname meaning</strong> of 张 (Zhang) comes from archery, while 史 (Shǐ) means "historian."
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Natural Elements</h3>
                <p className="text-gray-700 text-sm">
                  Many surnames reference natural elements. The <strong>Chinese last name meaning</strong> of 李 (Li) refers to the plum tree, while 杨 (Yáng) means "poplar tree."
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Virtue-Based</h3>
                <p className="text-gray-700 text-sm">
                  Some surnames reflect virtues or positive qualities. Understanding these <strong>Chinese surname meanings</strong> reveals the values important to Chinese culture.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Explore Chinese Last Names</h2>
            <form onSubmit={handleGenerateName} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors font-medium"
              >
                Generate Chinese Name →
              </button>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/chinese-last-names" className="text-purple-600 hover:underline font-medium">
                  Browse 100+ Chinese Last Names →
                </Link>
              </li>
              <li>
                <Link to="/blog/chinese-family-names-meanings" className="text-purple-600 hover:underline font-medium">
                  Chinese Family Names History →
                </Link>
              </li>
            </ul>
          </div>
        </article>
      </div>
    </>
  );
};

export default BlogPost17;

